/**
 * API Request Deduplication and Caching Utility
 * Prevents redundant API calls caused by React Strict Mode and component re-renders
 */

class ApiCache {
  constructor() {
    // Cache storage for completed requests
    this.cache = new Map();
    
    // In-flight requests to prevent duplicates
    this.inFlightRequests = new Map();
    
    // Default cache TTL (Time To Live) in milliseconds
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes
    
    // Short TTL for frequently changing data
    this.shortTTL = 30 * 1000; // 30 seconds
  }

  /**
   * Generate cache key from request config
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {object} params - Query parameters
   * @returns {string} - Cache key
   */
  generateKey(method, url, params = {}) {
    const sortedParams = params 
      ? JSON.stringify(Object.keys(params).sort().reduce((acc, key) => {
          acc[key] = params[key];
          return acc;
        }, {}))
      : '';
    return `${method.toUpperCase()}:${url}:${sortedParams}`;
  }

  /**
   * Check if cached data is still valid
   * @param {object} cachedData - Cached data with timestamp
   * @returns {boolean}
   */
  isValid(cachedData) {
    if (!cachedData) return false;
    const now = Date.now();
    return now - cachedData.timestamp < cachedData.ttl;
  }

  /**
   * Get cached data if valid
   * @param {string} key - Cache key
   * @returns {any|null} - Cached data or null
   */
  get(key) {
    const cachedData = this.cache.get(key);
    if (this.isValid(cachedData)) {
      return cachedData.data;
    }
    // Remove expired cache
    this.cache.delete(key);
    return null;
  }

  /**
   * Set cached data
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {number} ttl - Time to live in milliseconds
   */
  set(key, data, ttl = this.defaultTTL) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  /**
   * Get in-flight request promise
   * @param {string} key - Cache key
   * @returns {Promise|null}
   */
  getInFlight(key) {
    return this.inFlightRequests.get(key) || null;
  }

  /**
   * Set in-flight request promise
   * @param {string} key - Cache key
   * @param {Promise} promise - Request promise
   */
  setInFlight(key, promise) {
    this.inFlightRequests.set(key, promise);

    // Clean up after request completes or fails. Only drop the entry if it is
    // still THIS promise — clearAll() (on login/logout) may have evicted it and
    // a newer request may already own the key.
    promise.finally(() => {
      if (this.inFlightRequests.get(key) === promise) {
        this.inFlightRequests.delete(key);
      }
    });
  }

  /**
   * Clear cache entry
   * @param {string} key - Cache key
   */
  clear(key) {
    this.cache.delete(key);
  }

  /**
   * Clear all cache.
   *
   * Also drops the in-flight map: clearAll() is called on login/logout, and a
   * request still in flight from the PREVIOUS session must never be handed to
   * the new one via deduplication (it carries the old user's data and the old
   * session's refreshed-token header). The pending promises still resolve for
   * their original callers; they are just no longer joinable.
   */
  clearAll() {
    this.cache.clear();
    this.inFlightRequests.clear();
  }

  /**
   * Clear cache by URL pattern
   * @param {string|RegExp} pattern - URL pattern to match
   */
  clearByPattern(pattern) {
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
    const keysToDelete = [];
    
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Get cache statistics
   * @returns {object}
   */
  getStats() {
    return {
      cacheSize: this.cache.size,
      inFlightRequests: this.inFlightRequests.size,
      cachedKeys: Array.from(this.cache.keys())
    };
  }
}

// Singleton instance
const apiCache = new ApiCache();

/**
 * Wrapper for axios requests with caching and deduplication
 * @param {function} axiosRequest - Axios request function
 * @param {object} options - Options
 * @returns {Promise} - Request promise
 */
export const cachedRequest = async (axiosRequest, options = {}) => {
  const {
    method = 'GET',
    url = '',
    params = {},
    cache = true,
    ttl = apiCache.defaultTTL,
    deduplicate = true
  } = options;

  // Only cache GET requests by default
  const shouldCache = cache && method.toUpperCase() === 'GET';
  const shouldDeduplicate = deduplicate;

  // Generate cache key
  const cacheKey = apiCache.generateKey(method, url, params);

  // Return cached data if valid
  if (shouldCache) {
    const cachedData = apiCache.get(cacheKey);
    if (cachedData) {
      return Promise.resolve(cachedData);
    }
  }

  // Return in-flight request if exists (deduplication)
  if (shouldDeduplicate) {
    const inFlightRequest = apiCache.getInFlight(cacheKey);
    if (inFlightRequest) {
      return inFlightRequest;
    }
  }

  // Make new request
  const requestPromise = axiosRequest()
    .then(response => {
      // Cache successful response
      if (shouldCache) {
        apiCache.set(cacheKey, response, ttl);
      }
      return response;
    })
    .catch(error => {
      // Don't cache errors
      throw error;
    });

  // Track in-flight request
  if (shouldDeduplicate) {
    apiCache.setInFlight(cacheKey, requestPromise);
  }

  return requestPromise;
};

/**
 * Hook for clearing cache after mutations
 * @param {string|string[]} patterns - URL patterns to clear
 */
export const invalidateCache = (patterns) => {
  if (Array.isArray(patterns)) {
    patterns.forEach(pattern => apiCache.clearByPattern(pattern));
  } else {
    apiCache.clearByPattern(patterns);
  }
};

/**
 * React hook for cached API calls
 * @returns {object} - Cache utilities
 */
export const useApiCache = () => {
  return {
    invalidate: invalidateCache,
    clear: (key) => apiCache.clear(key),
    clearAll: () => apiCache.clearAll(),
    stats: () => apiCache.getStats()
  };
};

export default apiCache;

